import { AxiosError } from 'axios'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_REGION_BANK_HEADER_VALUE,
} from '@/constants'
import { useNomenclatureEditorFeedStore } from '@/stores/feed/nomenclatureEditorFeedStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import type {
  ApiErrorDetail,
  ApiErrorResponseData,
  CharacteristicFilterWithRange,
  EntityFilterType,
  NomenclatureEditorFeedProductInterface,
  SearchNomenclatureRequestBody,
  SearchResponseData,
} from '@/types'
import api from '@/utils/api/api'
import getGeneralErrorMessage from '@/utils/api/errors/getGeneralErrorMessage'
import { unwrapSearchResult } from '@/utils/api/unwrapSearchResult'
import {
  cleanCharacteristics,
  cleanFilterItems,
  cleanTechnicalSuffix,
} from '@/utils/catalog/cleanDescription'
import { hasUniformEligibleSlottedFilter } from '@/utils/search/characteristics/slottedCharacteristics'
import { buildCharacteristicFilterBody } from '@/utils/search/request/buildSearchRequestBody'

export default function useNomenclatureEditorSearch() {
  const {
    setProducts,
    setProductsLoading,
    setPagination,
    setPaginatedTableLoading,
  } = useNomenclatureEditorFeedStore((state) => state)

  const {
    setSearchTerm,
    setCurrentlySearchableBrands,
    setCurrentlySearchableCategories,
    setCurrentlySearchableNomenclatureGroupId,
    setCurrentlySearchableCharacteristics,
    setWithLeftovers,
    setSearchError,
    setServerChosenFilters,
    setAvailableFilters,
    setWithDiscontinued,
    setCurrentlySearchableParents,
    setInitialFilters,
  } = useSearchStore((state) => state)

  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  return (params: {
    query: string
    categories: EntityFilterType[]
    brands: EntityFilterType[]
    parents?: EntityFilterType[]
    nomenclatureGroupId: string
    page: number
    characteristicFilters?: CharacteristicFilterWithRange[]
    withLeftovers: boolean
    withDiscontinued?: boolean
    matchUniformSlottedCharacteristics?: boolean
    triggerNavigation?: boolean
    productCodes?: number[]
    isInitial?: boolean
    parentsFromUrl?: boolean
  }) => {
    const {
      query,
      categories,
      brands,
      parents = [],
      nomenclatureGroupId,
      page,
      characteristicFilters = [],
      withLeftovers = false,
      withDiscontinued = true,
      matchUniformSlottedCharacteristics = useSearchStore.getState()
        .matchUniformSlottedCharacteristics,
      triggerNavigation = true,
      productCodes,
      isInitial = false,
      parentsFromUrl = false,
    } = params

    const trimmedQuery = query.trim().replace(/\s+/g, ' ')

    const shouldMatchUniformSlotted =
      matchUniformSlottedCharacteristics &&
      hasUniformEligibleSlottedFilter(characteristicFilters)

    // Логика определения: поиск по коду или по тексту
    const isNumericQuery =
      /^\d+$/.test(trimmedQuery) && trimmedQuery.length >= 4
    const effectiveProductCodes =
      productCodes || (isNumericQuery ? [Number(trimmedQuery)] : undefined)
    const effectiveQuery = isNumericQuery ? '' : trimmedQuery

    setSearchTerm(trimmedQuery)
    setCurrentlySearchableCategories(categories)
    setCurrentlySearchableNomenclatureGroupId(nomenclatureGroupId)
    setCurrentlySearchableBrands(brands)
    setCurrentlySearchableParents(parents)
    setCurrentlySearchableCharacteristics(characteristicFilters)
    setWithLeftovers(withLeftovers)
    setWithDiscontinued(withDiscontinued)
    useSearchStore
      .getState()
      .setMatchUniformSlottedCharacteristics(shouldMatchUniformSlotted)

    // 1. Синхронизация URL
    if (triggerNavigation) {
      const urlParams = new URLSearchParams()

      if (trimmedQuery) urlParams.set('query', trimmedQuery)
      if (effectiveProductCodes?.length)
        urlParams.set('productCodes', effectiveProductCodes.join(','))
      if (nomenclatureGroupId) urlParams.set('groupId', nomenclatureGroupId)
      if (categories.length)
        urlParams.set('categoryIds', categories.map((c) => c.id).join(','))
      if (brands.length)
        urlParams.set('brandIds', brands.map((b) => b.id).join(','))
      if (parents.length)
        urlParams.set('parentIds', parents.map((p) => p.id).join(','))

      urlParams.set('leftovers', String(withLeftovers))
      urlParams.set('discontinued', String(withDiscontinued))
      urlParams.set('page', String(page))
      if (shouldMatchUniformSlotted) {
        urlParams.set('matchUniformSlottedCharacteristics', 'true')
      }

      // Обработка характеристик для URL
      characteristicFilters.forEach((filter) => {
        if (filter.type === 'NUMERIC' && filter.chosenRange) {
          if (filter.chosenRange.min != null)
            urlParams.append(
              `ranges[${filter.code}].min`,
              String(filter.chosenRange.min)
            )
          if (filter.chosenRange.max != null)
            urlParams.append(
              `ranges[${filter.code}].max`,
              String(filter.chosenRange.max)
            )
        }

        const values = [
          ...(filter.options || []).map((o) => String(o.value)),
          ...(filter.chosenOptions || []),
        ]
        values.forEach((val) => {
          urlParams.append(`filter[${filter.code}]`, val)
        })
      })

      if (urlParams.toString() !== searchParams.toString()) {
        if (location.pathname !== '/nomenclature-feed') {
          navigate(
            { pathname: '/nomenclature-feed', search: urlParams.toString() },
            { replace: true }
          )
        } else {
          setSearchParams(urlParams, { replace: true })
        }
        return
      }
    }

    if (page === 0) setProductsLoading(true)
    else setPaginatedTableLoading(true)

    // Подготовка ID родительских групп
    const allParentIds = [...parents.map((p) => p.id)]
    if (nomenclatureGroupId) allParentIds.push(nomenclatureGroupId)

    // Записка §10: `/nomenclatures/raw-search` тоже перешёл на POST с JSON-телом
    // `RawSearchNomenclatureRequest` — GET больше не биндит `filter[...]`.
    const { filter, ranges } = buildCharacteristicFilterBody(
      characteristicFilters
    )
    const requestBody: SearchNomenclatureRequestBody = {
      query: effectiveQuery,
      productCodes: effectiveProductCodes || [],
      page: page,
      pageSize: DEFAULT_PAGE_SIZE,
      exactMatch: false,
      withLeftovers: false,
      withDiscontinued: withDiscontinued,
      matchUniformSlottedCharacteristics: shouldMatchUniformSlotted,
      categoryIds: categories.map((c) => c.id),
      brandIds: brands.map((b) => b.id),
      parentIds: allParentIds,
      filter,
      ranges,
    }

    api
      .post<SearchResponseData<NomenclatureEditorFeedProductInterface>>(
        '/nomenclatures/raw-search',
        requestBody,
        {
          headers: { 'Region-Bank': DEFAULT_REGION_BANK_HEADER_VALUE },
        }
      )
      .then((response) => {
        const result = unwrapSearchResult(response.data)
        const { chosenFilters, availableFilters, pagedProducts } = result

        const cleanedChosen = {
          ...chosenFilters,
          brands: cleanFilterItems(chosenFilters?.brands),
          categories: cleanFilterItems(chosenFilters?.categories),
          parents: cleanFilterItems(chosenFilters?.parents),
          characteristics: cleanCharacteristics(chosenFilters?.characteristics),
        }

        // Логика подхвата родительской группы для хлебных крошек (пути) при поиске по коду
        if (
          effectiveProductCodes &&
          effectiveProductCodes.length > 0 &&
          (pagedProducts.content?.length ?? 0) > 0
        ) {
          const firstProduct = pagedProducts.content?.[0]
          const groupFromProduct = firstProduct?.nomenclatureDto?.parentModel

          if (
            groupFromProduct &&
            (!cleanedChosen.parents || cleanedChosen.parents.length === 0)
          ) {
            const syntheticParent = {
              id: groupFromProduct.id,
              description: cleanTechnicalSuffix(groupFromProduct.description),
              count: 0,
            }
            cleanedChosen.parents = [syntheticParent]
            // Устанавливаем ID группы, чтобы FeedSearchTerm построил путь
            setCurrentlySearchableNomenclatureGroupId(groupFromProduct.id)
          }
        }

        // Установка примененных фильтров
        setServerChosenFilters(cleanedChosen)
        if (cleanedChosen.brands)
          setCurrentlySearchableBrands(cleanedChosen.brands)
        if (cleanedChosen.categories)
          setCurrentlySearchableCategories(cleanedChosen.categories)
        if (cleanedChosen.parents)
          setCurrentlySearchableParents(cleanedChosen.parents)
        if (cleanedChosen.characteristics)
          setCurrentlySearchableCharacteristics(cleanedChosen.characteristics)

        if (isInitial) {
          setInitialFilters({
            searchTerm: trimmedQuery,
            categories: cleanedChosen.categories ?? categories,
            brands: cleanedChosen.brands ?? brands,
            productCodes: effectiveProductCodes ?? [],
            parents: parentsFromUrl ? [] : (cleanedChosen.parents ?? []),
          })
        }

        // Установка доступных фильтров
        if (availableFilters) {
          const cleanedAvailable = {
            ...availableFilters,
            brands: cleanFilterItems(availableFilters.brands),
            categories: cleanFilterItems(availableFilters.categories),
            parents: cleanFilterItems(availableFilters.parents),
            characteristics: cleanCharacteristics(
              availableFilters.characteristics
            ),
          }
          setAvailableFilters(cleanedAvailable)
        }

        const content = pagedProducts.content || []
        if (params.page > 0) {
          const currentProducts =
            useNomenclatureEditorFeedStore.getState().products
          setProducts([...currentProducts, ...content])
        } else {
          setProducts(content)
        }
        setSearchError('')
        setPagination({
          current: page + 1,
          pageSize: DEFAULT_PAGE_SIZE,
          total: pagedProducts.totalElements || 0,
        })
      })
      .catch((error) => {
        setProducts([])

        let errorMessage = getGeneralErrorMessage(error)
        let errorDetails: ApiErrorDetail[] = []
        let traceId: string | undefined

        if (error instanceof AxiosError) {
          traceId = (error.response?.data as ApiErrorResponseData | undefined)
            ?.traceId

          if (!error.response) {
            if (error.code === 'ERR_NETWORK') {
              errorMessage =
                'Ошибка доступа к внешнему сервису. Обратитесь к администратору.'
            }
          } else if (error.response.data) {
            const data = error.response.data as ApiErrorResponseData
            if (data.message) errorMessage = data.message
            if (data.details) errorDetails = data.details
          }
        }

        setSearchError({
          message: errorMessage,
          details: errorDetails,
          traceId: traceId,
        })
      })
      .finally(() => {
        setProductsLoading(false)
        setPaginatedTableLoading(false)
      })
  }
}
